import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, XCircle, ArrowRight, Lightbulb, Volume2, MessageSquare, Palette, Image as ImageIcon, FileText } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";

const ICON_BY_TYPE = {
  text: FileText, illustration: ImageIcon, comic: ImageIcon,
  audio: Volume2, message: MessageSquare, artwork: Palette, video: ImageIcon,
};

export default function ExercisePlayer() {
  const params = useParams();
  const location = useLocation();
  const sessionId = params.sessionId;
  const kind = params.kind || (location.pathname === "/review" ? "review" : location.pathname === "/today/play" ? "today" : undefined);
  const nav = useNavigate();
  const { speak } = useTheme();
  const [exercises, setExercises] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null); // {correct, feedback, hint, error_type, correct_choice_id}
  const [attemptNumber, setAttemptNumber] = useState(1);
  const [hintUsed, setHintUsed] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const t0 = useRef(Date.now());

  const label = kind === "pre" ? "Evaluación PRE" : kind === "post" ? "Evaluación POST" : kind === "review" ? "Repaso adaptativo" : kind === "today" ? "Mi entrenamiento de hoy" : `Sesión ${sessionId}`;

  const loadUrl = kind === "today"
    ? "/training/today/exercises"
    : kind === "review"
      ? "/assessment/review"
      : kind
        ? `/assessment/${kind}`
        : `/sessions/${sessionId}/exercises`;

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get(loadUrl);
    setExercises(res.data);
    setLoading(false);
    setIdx(0);
    setSelected(null);
    setResult(null);
    setAttemptNumber(1);
    setHintUsed(false);
    setDone(false);
    t0.current = Date.now();
  }, [loadUrl]);

  useEffect(() => { load(); }, [load]);

  const current = exercises[idx];
  const [lazyImg, setLazyImg] = useState(null);
  const [lazyChoiceImgs, setLazyChoiceImgs] = useState(null); // for image_choice: array of {id, image_url}
  const [imgLoading, setImgLoading] = useState(false);

  useEffect(() => {
    setAudioUrl(null);
    setLazyImg(null);
    setLazyChoiceImgs(null);
    // Lazy prewarm images for visual exercises that don't have image_url yet
    if (current && current.stimulus_meta?.image_prompt && !current.stimulus_meta?.image_url) {
      setImgLoading(true);
      api.post(`/ai/prewarm-exercise/${current.id}`)
        .then((r) => setLazyImg(r.data?.stimulus_meta?.image_url || null))
        .catch(() => {})
        .finally(() => setImgLoading(false));
    }
    // Lazy prewarm choice images (image_choice stimulus_type)
    const ci = current?.stimulus_meta?.choice_images;
    if (ci && ci.some((c) => !c.image_url)) {
      setImgLoading(true);
      api.post(`/ai/prewarm-exercise/${current.id}`)
        .then((r) => setLazyChoiceImgs(r.data?.stimulus_meta?.choice_images || null))
        .catch(() => {})
        .finally(() => setImgLoading(false));
    }
    if (current?.stimulus_type === "audio") {
      // Prefer pre-warmed audio if available
      const cached = current.stimulus_meta?.audio_url;
      if (cached) {
        setAudioUrl(cached);
      } else {
        setAudioLoading(true);
        api.post("/ai/audio", {
          text: current.stimulus_text || "",
          voice: current.stimulus_meta?.voice || "nova",
          emotion: current.stimulus_meta?.audio_emotion || null,
        })
          .then((r) => setAudioUrl(r.data.url))
          .catch(() => {})
          .finally(() => setAudioLoading(false));
      }
    }
    if (current) speak(current.prompt);
    // Prefetch the NEXT exercise's image while the user is on this one
    const nxt = exercises[idx + 1];
    if (nxt && (nxt.stimulus_meta?.image_prompt || nxt.stimulus_meta?.choice_images) &&
        !(nxt.stimulus_meta?.image_url)) {
      api.post(`/ai/prewarm-exercise/${nxt.id}`).catch(() => {});
    }
    t0.current = Date.now();
  }, [idx, current, speak, exercises]);

  const submit = async () => {
    if (!selected || !current) return;
    try {
      const res = await api.post("/attempts", {
        exercise_id: current.id,
        session_id: current.session_id,
        chosen_id: selected,
        response_time_ms: Date.now() - t0.current,
        hint_used: hintUsed,
        attempt_number: attemptNumber,
      });
      setResult(res.data);
      if (res.data.correct) speak("Correcto. " + (res.data.feedback || ""));
      else speak("Vuelve a intentarlo. " + (res.data.hint || ""));
    } catch (e) {
      toast.error("No se pudo registrar el intento");
    }
  };

  const next = async () => {
    if (idx + 1 >= exercises.length) {
      // Finalize only for pre/post assessments
      if (kind === "pre" || kind === "post") {
        try {
          await api.post(`/assessment/${kind}/finalize`);
          toast.success(`Evaluación ${kind.toUpperCase()} finalizada`);
        } catch { /* ignore */ }
      }
      setDone(true);
      return;
    }
    setIdx((i) => i + 1);
    setSelected(null);
    setResult(null);
    setAttemptNumber(1);
    setHintUsed(false);
  };

  const tryAgain = () => {
    setResult(null);
    setSelected(null);
    setAttemptNumber(2);
    setHintUsed(true);
  };

  if (loading) return <div className="p-8">Cargando ejercicios...</div>;
  if (!exercises.length)
    return (
      <div className="p-8 text-center">
        <p>No hay ejercicios disponibles.</p>
        <Button className="mt-4" onClick={() => nav(-1)}>Volver</Button>
      </div>
    );

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto text-center py-12" data-testid="exercise-done">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-primary text-primary-foreground grid place-items-center">
          <CheckCircle2 size={30} />
        </div>
        <h1 className="heading text-3xl font-extrabold mt-4">{label} completada</h1>
        <p className="text-foreground/70 mt-2 max-w-md mx-auto">
          {kind === "pre" || kind === "post"
            ? "Hemos actualizado tu perfil cognitivo. Consulta tu panel para ver los resultados."
            : kind === "today"
              ? "Has completado tu entrenamiento diario. Vuelve mañana para una nueva sesión adaptativa."
              : kind === "review"
                ? "Has revisado tus últimos fallos. Sigue así para consolidar cada dominio."
                : "Buen trabajo. Puedes continuar con otras sesiones o revisar tu progreso."}
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Button onClick={() => nav("/dashboard")} data-testid="btn-back-dashboard">Volver al panel</Button>
          <Button variant="outline" onClick={() => nav("/sessions")}>Ver sesiones</Button>
        </div>
      </motion.div>
    );
  }

  const Icon = ICON_BY_TYPE[current.stimulus_type] || FileText;
  const progressPct = Math.round(((idx) / exercises.length) * 100);
  const isMessage = current.stimulus_type === "message";
  const isArtwork = current.stimulus_type === "artwork";

  return (
    <div className="max-w-3xl mx-auto pb-16" data-testid="exercise-player">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary font-semibold">{label}</p>
            <h1 className="heading text-2xl sm:text-3xl font-bold mt-1">Ejercicio {idx + 1} de {exercises.length}</h1>
          </div>
          <Badge variant="outline" className="capitalize">
            <Icon size={12} className="mr-1" /> {current.stimulus_type}
          </Badge>
        </div>
        <Progress value={progressPct} className="mt-3 h-2" />
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={current.id}
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
          className="space-y-4"
        >
          <Card className={`rounded-2xl border-border ${result && !result.correct && attemptNumber === 1 ? "shake" : ""}`}>
            <CardContent className="p-6 sm:p-8 space-y-6">
              <div>
                <p className="text-sm uppercase tracking-widest text-muted-foreground font-semibold">Dominio</p>
                <p className="mt-1 font-medium capitalize">{current.domain.replace(/_/g, " ")}</p>
              </div>

              <div>
                <p className="text-sm uppercase tracking-widest text-muted-foreground font-semibold mb-2">Instrucción</p>
                <h2 className="heading text-xl sm:text-2xl font-bold leading-snug">{current.prompt}</h2>
              </div>

              {(current.stimulus_meta?.image_url || lazyImg) && (
                current.domain === "microexpressions" ? (
                  <MicroFlash
                    src={`${process.env.REACT_APP_BACKEND_URL}${current.stimulus_meta?.image_url || lazyImg}`}
                    exerciseId={current.id}
                  />
                ) : (
                  <div className="rounded-xl overflow-hidden border border-border bg-secondary/40" data-testid="stimulus-visual">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${current.stimulus_meta?.image_url || lazyImg}`}
                      alt="Estímulo visual"
                      className={`w-full ${current.stimulus_type === "comic" ? "max-h-[560px] object-contain bg-white" : "max-h-96 object-cover"}`}
                      data-testid="stimulus-image" />
                  </div>
                )
              )}
              {!current.stimulus_meta?.image_url && !lazyImg && imgLoading && (
                <div className="rounded-xl border border-border p-6 bg-secondary/50 text-center text-sm text-muted-foreground" data-testid="stimulus-image-loading">
                  Preparando estímulo visual... (5–10 s)
                </div>
              )}

              {current.stimulus_type === "video" && current.stimulus_meta?.youtube_id && (
                <div className="rounded-xl overflow-hidden border border-border bg-black" data-testid="video-stimulus">
                  <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src={`https://www.youtube.com/embed/${current.stimulus_meta.youtube_id}?rel=0&modestbranding=1${current.stimulus_meta.start_seconds ? `&start=${current.stimulus_meta.start_seconds}` : ""}${current.stimulus_meta.end_seconds ? `&end=${current.stimulus_meta.end_seconds}` : ""}`}
                      title="Estímulo en vídeo"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      data-testid="video-iframe"
                    />
                  </div>
                </div>
              )}

              {current.stimulus_text && (
                <div className={`rounded-xl border p-5 ${
                  isMessage ? "bg-secondary border-primary/30" : isArtwork ? "bg-accent/10 border-accent/30" : "bg-secondary border-border"
                }`}>
                  <p className={`text-lg leading-relaxed whitespace-pre-line ${isMessage ? "font-mono" : ""}`} data-testid="stimulus-text">
                    {current.stimulus_text}
                  </p>
                </div>
              )}

              {current.stimulus_type === "audio" && (
                <div className="rounded-xl border border-border p-5 bg-secondary flex items-center gap-4" data-testid="audio-stimulus">
                  <Volume2 className="text-primary" size={24} />
                  <div className="flex-1">
                    {audioLoading && <span className="text-sm text-muted-foreground">Generando audio...</span>}
                    {audioUrl && !audioLoading && (
                      <audio controls className="w-full" src={`${process.env.REACT_APP_BACKEND_URL}${audioUrl}`} data-testid="audio-player" />
                    )}
                    {!audioLoading && !audioUrl && (
                      <p className="text-sm text-muted-foreground">
                        (Prosodia esperada: <b className="capitalize">{current.stimulus_meta?.audio_emotion || "neutra"}</b>. Audio no disponible; guíate por la descripción.)
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Choices */}
              {(() => {
                const ci = (lazyChoiceImgs || current.stimulus_meta?.choice_images || []);
                const isImageChoice = current.stimulus_type === "image_choice" && ci.length > 0;
                if (isImageChoice) {
                  const gridCols = ci.length === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-3";
                  const allReady = ci.every((c) => c.image_url);
                  return (
                    <div className={`grid ${gridCols} gap-4`} role="radiogroup" data-testid="choices-group">
                      {ci.map((c) => {
                        const isSel = selected === c.id;
                        const isCorrect = result?.correct_choice_id === c.id;
                        const isWrong = result && selected === c.id && !result.correct;
                        return (
                          <button
                            key={c.id}
                            role="radio"
                            aria-checked={isSel}
                            disabled={!!result || !c.image_url}
                            onClick={() => setSelected(c.id)}
                            data-testid={`choice-${c.id}`}
                            className={`group relative rounded-2xl border-2 overflow-hidden bg-secondary/40 transition-all cursor-highlight
                              ${isSel && !result ? "border-primary ring-4 ring-primary/20 scale-[1.02]" : "border-border hover:border-primary/60 hover:scale-[1.01]"}
                              ${result && isCorrect ? "border-primary ring-4 ring-primary/30" : ""}
                              ${isWrong ? "border-destructive ring-4 ring-destructive/20" : ""}
                              disabled:opacity-60 disabled:cursor-not-allowed
                            `}
                          >
                            {c.image_url ? (
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${c.image_url}`}
                                alt={`Opción ${c.id.toUpperCase()}`}
                                className="w-full aspect-square object-cover"
                                data-testid={`choice-image-${c.id}`}
                              />
                            ) : (
                              <div className="w-full aspect-square flex items-center justify-center text-xs text-muted-foreground p-4 text-center">
                                Preparando imagen…
                              </div>
                            )}
                            <div className={`absolute top-2 left-2 inline-flex items-center justify-center w-10 h-10 rounded-full font-extrabold text-base shadow-lg
                              ${result && isCorrect ? "bg-primary text-primary-foreground" :
                                isWrong ? "bg-destructive text-destructive-foreground" :
                                isSel ? "bg-primary text-primary-foreground" : "bg-white/95 text-foreground border border-border"}`}>
                              {c.id.toUpperCase()}
                            </div>
                            {result && isCorrect && (
                              <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1.5 shadow-lg">
                                <CheckCircle2 size={20} />
                              </div>
                            )}
                            {isWrong && (
                              <div className="absolute top-2 right-2 bg-destructive text-destructive-foreground rounded-full p-1.5 shadow-lg">
                                <XCircle size={20} />
                              </div>
                            )}
                          </button>
                        );
                      })}
                      {!allReady && (
                        <div className="col-span-full text-center text-xs text-muted-foreground" data-testid="choice-images-loading">
                          Generando fotografías... (10–15 s)
                        </div>
                      )}
                    </div>
                  );
                }
                // Standard text-choice layout
                return (
                  <div className="grid grid-cols-1 gap-3" role="radiogroup" data-testid="choices-group">
                    {current.choices.map((c) => {
                      const isSel = selected === c.id;
                      const isCorrect = result?.correct_choice_id === c.id;
                      const isWrong = result && selected === c.id && !result.correct;
                      return (
                        <button
                          key={c.id}
                          role="radio"
                          aria-checked={isSel}
                          disabled={!!result}
                          onClick={() => setSelected(c.id)}
                          data-testid={`choice-${c.id}`}
                          className={`text-left w-full px-5 py-4 rounded-2xl border transition-colors flex items-center gap-3 cursor-highlight
                            ${isSel && !result ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/60"}
                            ${result && isCorrect ? "border-primary bg-primary/15" : ""}
                            ${isWrong ? "border-destructive bg-destructive/10" : ""}
                          `}
                        >
                          <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-bold border ${
                            result && isCorrect ? "bg-primary text-primary-foreground border-primary" :
                            isWrong ? "bg-destructive text-destructive-foreground border-destructive" :
                            isSel ? "bg-primary text-primary-foreground border-primary" : "border-border"
                          }`}>{c.id.toUpperCase()}</span>
                          <span className="text-base sm:text-lg font-medium flex-1">{c.label}</span>
                          {result && isCorrect && <CheckCircle2 className="text-primary" size={22} />}
                          {isWrong && <XCircle className="text-destructive" size={22} />}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Feedback */}
          {result && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid="feedback-box"
              className={`rounded-2xl border p-5 ${result.correct ? "border-primary/40 bg-primary/5" : "border-accent/40 bg-accent/10"}`}
            >
              <div className="flex items-start gap-3">
                {result.correct
                  ? <CheckCircle2 className="text-primary shrink-0" size={22} />
                  : <XCircle className="text-accent shrink-0" size={22} />}
                <div className="flex-1">
                  <p className="font-semibold heading">
                    {result.correct ? "Correcto" : attemptNumber === 1 ? "Todavía no" : "Sigue intentándolo"}
                  </p>
                  <p className="text-foreground/85 mt-1 leading-relaxed">{result.feedback}</p>
                  {result.hint && !result.correct && attemptNumber === 1 && (
                    <div className="mt-3 flex items-start gap-2 text-sm bg-warning/10 border border-warning/30 rounded-lg px-3 py-2">
                      <Lightbulb size={16} className="text-warning shrink-0 mt-0.5" />
                      <span><b>Pista:</b> {result.hint}</span>
                    </div>
                  )}
                  {!result.correct && result.error_type && result.error_type !== "none" && (
                    <div className="mt-2 text-xs uppercase tracking-wider text-muted-foreground">
                      Tipo de error: <span className="font-semibold">{result.error_type.replace(/_/g, " ")}</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3 justify-between items-center">
            <div className="text-sm text-muted-foreground">
              Intento {attemptNumber}/2 {hintUsed && "· Pista usada"}
            </div>
            <div className="flex gap-2">
              {!result && (
                <Button size="lg" onClick={submit} disabled={!selected} className="rounded-full h-12 px-6" data-testid="submit-answer-btn">
                  Comprobar respuesta
                </Button>
              )}
              {result && !result.correct && attemptNumber === 1 && (
                <Button size="lg" variant="outline" onClick={tryAgain} className="rounded-full h-12 px-6" data-testid="try-again-btn">
                  Intentar de nuevo con la pista
                </Button>
              )}
              {result && (result.correct || attemptNumber >= 2) && (
                <Button size="lg" onClick={next} className="rounded-full h-12 px-6" data-testid="next-btn">
                  {idx + 1 >= exercises.length ? "Finalizar" : "Siguiente"} <ArrowRight size={16} className="ml-1" />
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}


/**
 * Microexpression flash: shows a gray placeholder and briefly flashes the real image
 * for ~300 ms (like a real microexpression). The patient can replay as many times as needed.
 */
function MicroFlash({ src, exerciseId }) {
  const [visible, setVisible] = useState(false);
  const [count, setCount] = useState(0);
  const flash = () => {
    setVisible(true);
    setCount((c) => c + 1);
    setTimeout(() => setVisible(false), 300);
  };
  // Auto-flash the first time
  useEffect(() => {
    const t = setTimeout(flash, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseId]);
  return (
    <div className="rounded-xl overflow-hidden border border-border bg-secondary/40 relative" data-testid="microflash">
      <div className="w-full h-96 bg-neutral-200 dark:bg-neutral-800 grid place-items-center relative">
        <img
          src={src}
          alt="Microexpresión"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-100 ${visible ? "opacity-100" : "opacity-0"}`}
          data-testid="stimulus-image"
        />
        {!visible && (
          <div className="text-sm text-muted-foreground text-center px-6 relative z-10">
            Rostro neutro · pulsa <b>Reproducir</b> para ver la microexpresión (~300 ms)
          </div>
        )}
      </div>
      <div className="p-3 flex items-center justify-between bg-card border-t border-border">
        <span className="text-xs text-muted-foreground">Reproducciones: {count}</span>
        <button
          onClick={flash}
          className="px-4 h-9 rounded-full bg-primary text-primary-foreground text-sm font-semibold"
          data-testid="microflash-play"
        >
          {count === 0 ? "Reproducir" : "Volver a reproducir"}
        </button>
      </div>
    </div>
  );
}
